import {
    FC, useMemo, useEffect, useState
} from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import VoterCardFrontImage from '../../Assets/images/voter_card_2.png';
import PassportFrontImage from '../../Assets/images/passport_front.png';
import DrivingLicenseFrontImage from '../../Assets/images/driving_license_front.png';
import OthersImage from '../../Assets/images/others_front.png';
import TakeDocumentPhoto, {
    ITakeDocumentPhotoInstructions
} from '../../Components/TakeDocumentPhoto/TakeDocumentPhoto';
import DI from '../../../Utils/DI';
import Constants, {
    DOCUMENT_ORIENTATION,
    DOCUMENT_TYPES
} from '../../../Utils/Constants';
import { IFormStepProps } from '../PageWrapper/PageWrapper';
import IDocumentManager from '../../../Business/IDocumentManager';
import IUserManager from '../../../Business/UserManager/IUserManager';
import IChallengeManager from '../../../Business/IChallengeManager';
import useGaEventTracker from '../../Hooks/useGaEventTracker';
import IAnalyticsManager from '../../../Business/IAnalyticsManager';
import useStepContext from '../../Hooks/useStepContext';
import Utils from '../../../Utils/Utils';
import { ISteps } from '../../../Utils/Constants/FormSteps';
import ISessionManager from '../../../Business/ISessionManager';
import { ERROR_CODES, ERRORS_DESCRIPTION } from '../../../Utils/Constants/Errors';
import { ITempError } from '../AgentLogin/agent-login.types';

const OthersFront: FC<IFormStepProps> = ({ nextStepHandler }) => {
    const { t, i18n } = useTranslation();
    const documentManager: IDocumentManager = DI.get('DocumentManager');
    const userManager: IUserManager = DI.get('UserManager');
    const challengeManager: IChallengeManager = DI.get('ChallengeManager');
    const { screenTracker, eventTracker } = useGaEventTracker();
    const analyticsManager: IAnalyticsManager = DI.get('AnalyticsManager');
    const {
        currentFormStep,
        whitelistedUser,
        documentType,
        setDocumentType,
        documentDescription,
        setDocumentDescription,
        captureBackSide,
        setCaptureBackSide
    } = useStepContext();
    const {
        selectedDocumentId,
        selectedDocumentEn,
        selectedDocumentFr,
        identityDocuments
    } = currentFormStep?.PROPS || {};
    const sessionManager: ISessionManager = DI.get('SessionManager');
    const isAgentLogged: boolean | undefined = useMemo(
        () => sessionManager.getSession()?.getIsValid(),
        []
    );

    const [error, setError] = useState<ITempError>({
        hasError: false,
        errorCode: NaN
    });

    const currentLanguage = i18n.language;
    useEffect(() => {
        if (identityDocuments && Array.isArray(identityDocuments)) {
            const doc = identityDocuments.find((d) => d.documentType === documentType);
            if (doc?.documentType) {
                setDocumentType(doc.documentType);
                setDocumentDescription(currentLanguage === 'FR' ? doc.fr : doc.en);
                return;
            }
        }

        if (!documentDescription) {
            setDocumentDescription(
                currentLanguage === 'FR'
                    ? selectedDocumentFr || ''
                    : selectedDocumentEn || ''
            );
        }
    }, [
        identityDocuments,
        documentType,
        currentLanguage,
        setDocumentType,
        setDocumentDescription,
        selectedDocumentEn,
        selectedDocumentFr,
        documentDescription
    ]);
    const translationKey = useMemo(() => {
        switch (documentType) {
        case 'NEW_VOTER_CARD':
            return 'voterCardFrontV2';
        case 'OLD_VOTER_CARD':
            return 'voterCardFrontV1';
        case 'PASSPORT':
            return 'passportFront';
        case 'DRIVING_LICENSE':
            return 'drivingLicenseFront';
        default:
            return 'othersFront';
        }
    }, [documentType]);

    const instructionsArray: string[] = [];
    for (let i = 1; i <= 10; i++) {
        const key = `${translationKey}.instruction${i}`;
        const value = t(key, '');
        if (value && value !== key) {
            instructionsArray.push(value);
        }
    }

    const documentImageMap: Record<string, string> = {
        NEW_VOTER_CARD: VoterCardFrontImage,
        OLD_VOTER_CARD: VoterCardFrontImage,
        PASSPORT: PassportFrontImage,
        DRIVING_LICENSE: DrivingLicenseFrontImage,
        OTHERS: OthersImage
    };
    const documentImage = documentImageMap[documentType ?? ''] || OthersImage;

    const takeDocumentPhotoInstructions: ITakeDocumentPhotoInstructions = {
        key: translationKey,
        instructionsLength: instructionsArray.length
    };

    const onChangeFunction = (file: File) => {
        if (!documentType) return;
        documentManager
            .processDocumentData(
                String(userManager.getMsisdn()),
                file,
                documentType.toLowerCase(),
                DOCUMENT_ORIENTATION.FRONT,
                whitelistedUser
            )
            .then(
                () => {
                    const elapsedTime = moment()
                        .subtract(
                            moment.unix(analyticsManager.getDocumentUploadInitTime()).unix(),
                            'milliseconds'
                        )
                        .format('mm:ss');
                    eventTracker({
                        name: 'OCR',
                        objectValue: {
                            SUCCESS: `TOC: ${currentFormStep.KEY} - ${Utils.getTimeInterval(
                                elapsedTime
                            )}`
                        }
                    });
                    eventTracker({
                        name: 'OCR',
                        objectValue: {
                            SUCCESS: `ATTEMPTS - ${
                                currentFormStep.KEY
                            } - ${analyticsManager.getDocumentUploadAttemptsCounter()}`
                        }
                    });
                    analyticsManager.setDocumentUploadAttemptsCounter(1);

                    if (captureBackSide === true) {
                        nextStepHandler({
                            ...Constants.FORM_STEPS.OTHERS_BACK,
                            PROPS: {
                                isWhitelistedUser: whitelistedUser,
                                selectedDocumentId,
                                selectedDocumentEn,
                                selectedDocumentFr,
                                captureBackSide,
                                documentType,
                                documentDescription
                            }
                        });
                    } else if (challengeManager.retrieveCapturedSelfie()) {
                        screenTracker({
                            screen: '6.REVIEW_DETAILS',
                            screenClass: 'GENERAL_FORM'
                        });
                        nextStepHandler(Constants.FORM_STEPS.REVIEW_OTHERS);
                    } else {
                        screenTracker({
                            screen: '5.LIVENESS_CHECK',
                            screenClass: 'LIVENESS_CHECK'
                        });
                        if (isAgentLogged) {
                            nextStepHandler(Constants.FORM_STEPS.CUSTOMER_VALIDATION);
                            return;
                        }
                        nextStepHandler(Constants.FORM_STEPS.SELFIE);
                    }
                    // }
                }
            ).catch((errorCode) => {
                const numericCode = Number(errorCode);
                const isServerDown = [500, 503, 504].includes(numericCode);
                if (isServerDown) {
                    ERRORS_DESCRIPTION[numericCode] = {
                        title: t('error.serviceUnavailable'),
                        description: t('error.serviceUnavailableSubtitle')
                    };

                    setError({
                        hasError: true,
                        errorCode: numericCode
                    });
                    return;
                }
                if (!numericCode || isNaN(numericCode)) {
                    ERRORS_DESCRIPTION[ERROR_CODES.NETWORK_ERROR] = {
                        title: t('error.networkError'),
                        description: t('error.networkErrorSubtitle')
                    };

                    setError({
                        hasError: true,
                        errorCode: ERROR_CODES.NETWORK_ERROR
                    });
                    return;
                }

                setError((prev) => ({ ...prev, hasError: true, errorCode }));
            });
    };

    return (
        <>
            <TakeDocumentPhoto
                title={documentDescription}
                image={documentImage}
                instructionText={t(`${translationKey}.instructionText`, '')}
                captureTitle={t(`${translationKey}.captureTitle`)}
                validateTitle={t(`${translationKey}.validateTitle`)}
                buttonText={t(`${translationKey}.buttonText`)}
                onChangeFn={onChangeFunction}
                takeDocumentPhotoInstructions={takeDocumentPhotoInstructions}
                instructions={instructionsArray}
            />
        </>
    );
};

export default OthersFront;
